import {
  Action,
  createAction,
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
  current,
} from '@reduxjs/toolkit';
import moment from 'moment';

import {
  AddressApi,
  AddressData,
  BillApi,
  EntityApi,
  BillData,
  ProductApi,
  FindEntityCustomShippingFeeToCreateBillQuery,
  EntityCustomShippingFeesApi,
  EntityDistanceShippingFeesApi,
  FindEntityDistanceShippingFeeToCreateBillQuery,
  FindEntityShippingPolicyItemToCreateBillQuery,
  EntityLocationShippingFeesApi,
  AdminAddressApi,
} from 'api';
import { RootState } from 'app';
import { PROMOTION_TYPE } from 'containers/SaleManager/Discount/Discount/commons';
import {
  ADJUSTMENT_TYPE,
  compareAddress,
  DELIVERY_FEE_TYPE,
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  PartnerInfoType,
  SummaryBillType,
  SupplierInfoType,
} from 'utils';

import { ADDRESS_TYPE } from './addressSlice';

export const createBillSliceListenerMiddleware = createListenerMiddleware();

export const takeLastedCalcWholesaleProductItemWhenCreateBill = createAction<
  any | undefined
>('takeLastedCalcWholesaleProductItemWhenCreateBill');

createBillSliceListenerMiddleware.startListening({
  actionCreator: takeLastedCalcWholesaleProductItemWhenCreateBill,
  effect: async (action: Action, listenerApi: any) => {
    // cancel other running instances
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(800); // delay to wait next action before dispatching final one

    const { payload } = action as any;
    listenerApi.dispatch(
      checkBillItemQuantityForCreateBill({
        body: payload,
        isUpdatingProductItem: true,
      })
    );
  },
});

export const checkBillItemQuantityForCreateBill = createAsyncThunk(
  'createBill/checkBillItemQuantityForCreateBill',
  async (
    params: { body: any; isUpdatingProductItem?: boolean },
    { rejectWithValue }
  ) => {
    const { body } = params;
    try {
      const response = await BillApi.checkBillItemQuantity(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityToCreateBill = createAsyncThunk(
  'account/findEntityToCreateBill',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findEntityToCreateBill(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createDraftBill = createAsyncThunk(
  'createBill/createDraftBill',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await BillApi.createDraftBill(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createBill = createAsyncThunk(
  'createBill/createBill',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await BillApi.createBill(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForCreateBill = createAsyncThunk(
  'createBill/createAddressForCreateBill',
  async (param: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = param;
      const response = await AdminAddressApi.create(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAnonymousAddressForCreateBill = createAsyncThunk(
  'createBill/createAnonymousAddressForCreateBill',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await AddressApi.createWithType({
        ...body,
        type: ADDRESS_TYPE.ANONYMOUS_ENTITY,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeAddressForCreateBill = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('createBill/removeAddressForCreateBill', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const updateAddressForCreateBill = createAsyncThunk<
  any,
  { id: string; body: AddressData },
  { rejectValue: { message: string } }
>(
  'createBill/updateAddressForCreateBill',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await AdminAddressApi.update(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findListAddressForCreateBill = createAsyncThunk(
  'createBill/findListAddressForCreateBill',
  async (param: { id; query }, { rejectWithValue }) => {
    try {
      const { id, query } = param;

      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductForCreateBill = createAsyncThunk<
  any,
  { body: any },
  { rejectValue: { message: string } }
>(
  'createBill/findProductForCreateBill',
  async ({ body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductToCreateBill(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityCustomShippingFeeToCreateBill = createAsyncThunk(
  'entityCustomShippingFee/findEntityCustomShippingFeeToCreateBill',
  async (
    option: FindEntityCustomShippingFeeToCreateBillQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityCustomShippingFeesApi.findToCreateBill(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityCustomShippingFeeToCreateBillFromOrder =
  createAsyncThunk(
    'entityCustomShippingFee/findEntityCustomShippingFeeToCreateBillFromOrder',
    async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
      try {
        const response = await EntityCustomShippingFeesApi.findToUpdateOrder(
          orderID,
          { isCreateBillCase: true }
        );

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findEntityDistanceShippingFeeToCreateBill = createAsyncThunk(
  'entityDistanceShippingFee/findEntityDistanceShippingFeeToCreateBill',
  async (
    option: FindEntityDistanceShippingFeeToCreateBillQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityDistanceShippingFeesApi.findToCreateBill(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityDistanceShippingFeeToCreateBillFromOrder =
  createAsyncThunk(
    'entityDistanceShippingFee/findEntityDistanceShippingFeeToCreateBillFromOrder',
    async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
      try {
        const response = await EntityDistanceShippingFeesApi.findToUpdateOrder(
          orderID,
          { isCreateBillCase: true }
        );

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findEntityShippingPolicyItemToCreateBill = createAsyncThunk(
  'entityShippingPolicyItem/findEntityShippingPolicyItemToCreateBill',
  async (
    option: FindEntityShippingPolicyItemToCreateBillQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityLocationShippingFeesApi.findToCreateBill(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityShippingPolicyItemToCreateBillFromOrder =
  createAsyncThunk(
    'entityShippingPolicyItem/findEntityShippingPolicyItemToCreateBillFromOrder',
    async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
      try {
        const response = await EntityLocationShippingFeesApi.findToUpdateOrder(
          orderID,
          { isCreateBillCase: true }
        );

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

const updateStore = (
  state: any,
  data: any,
  shouldUpdateProducts: boolean
): void => {
  const { selectedProducts, summaryBill, validationResult, totalQuantity } =
    data;

  if (shouldUpdateProducts) {
    let newSelectedProductItems = [...current(state).selectedProducts];

    newSelectedProductItems = [...newSelectedProductItems].map((item: any) => {
      const findSelectedProduct = selectedProducts.find(
        (selectedProduct: any) =>
          selectedProduct.productID == item.id &&
          selectedProduct.subID == item.subID
      );

      if (findSelectedProduct) {
        const { absoluteQuantity, relativeQuantity, reportType } =
          findSelectedProduct.reportQuantity;
        const { priceUnit, priceUnitList } = findSelectedProduct;

        return {
          ...item,
          ...findSelectedProduct,
          priceUnit: (priceUnitList ?? item.priceUnit).filter(
            (price: any) => price.isSelected
          ),
          selectedPriceUnit: priceUnit ?? item.selectedPriceUnit,
          oldPriceUnit: undefined,
          absoluteQuantity,
          relativeQuantity,
          reportType,
          proposalItemID: findSelectedProduct.proposalItemID,
          productPricePolicyID: findSelectedProduct.productPricePolicyID,
          productProposalPolicyID: findSelectedProduct.productProposalPolicyID,
          salePromotionConditions: findSelectedProduct.salePromotionConditions,
        };
      }

      return item;
    });
    state.selectedProducts = [...newSelectedProductItems];
  }

  state.deliveryFee = summaryBill?.deliveryFee ?? 0;
  state.deliveryFeeType =
    summaryBill?.deliveryFeeType ?? DELIVERY_FEE_TYPE.DEFAULT;
  state.summaryBill = summaryBill;
  state.promotions = (summaryBill?.adjustments || [])
    .filter(
      (item: any) => item.type == ADJUSTMENT_TYPE.PROMOTION && item.price > 0
    )
    .map((item: any) => item.info);
  state.validationResult = validationResult;
  state.totalCustomerQuantity = totalQuantity;
  state.totalPrice = Number(summaryBill?.totalPrice ?? 0);
  state.depositMoney = summaryBill?.depositMoney ?? {};
  state.paidMoney = summaryBill?.paidMoney ?? {};
  state.returnMoney = Number(summaryBill?.returnMoney ?? 0);
  state.deductedMoney = Number(summaryBill?.deductedMoney ?? 0);
  state.excessMoney = -Number(summaryBill?.excessMoney ?? 0);
  state.isDebt = summaryBill?.isDebt;
  state.debtMoney = Number(summaryBill?.debtMoney ?? 0);

  state.customShippingFees = (state.customShippingFees || []).map(item => {
    const foundItem = (
      summaryBill?.deliveryFeeDetail?.customShippingFees || []
    ).find(item1 => item1.id == item.id);
    if (foundItem) {
      return {
        ...item,
        ...foundItem,
        isSelected: true,
      };
    }
    return { ...item, isSelected: false };
  });
  state.shippingPolicyItems = (state.shippingPolicyItems || []).map(item => {
    if (
      summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem?.id == item.id
    ) {
      return {
        ...item,
        ...summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem,
        isSelected: true,
      };
    }
    return { ...item, isSelected: false };
  });
  state.distanceShippingFees = (state.distanceShippingFees || []).map(item => {
    if (
      summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee?.id == item.id
    ) {
      return {
        ...item,
        ...summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee,
        isSelected: true,
      };
    }
    return { ...item, isSelected: false };
  });
  state.locationShippingFee =
    summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem;
  state.distanceShippingFee =
    summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee;
};

const updateProductsWithValidationResult = (state: any): void => {
  const {
    deletedProductIDs,
    isPriceChanged,
    priceChangedProducts,
    isAddressChanged,
    changedAddress,
    isBillEntityShippingPolicyItemChanged,
    changedBillEntityShippingPolicyItem,
    isBillEntityDistanceShippingFeeChanged,
    changedBillEntityDistanceShippingFee,
    priceChangedBillItems,
    isReportQuantityChanged,
    reportQuantityChangedProducts,
    reportQuantityChangedBillItems,
    deletedProposalItemIDs,
    deletedProductPricePolicyIDs,
    deletedProductProposalPolicyIDs,
    isValidBillReturnMoney,
    isBillEntityCustomShippingFeesChanged,
    changedBillEntityCustomShippingFees,
  } = state.validationResult;
  if (isAddressChanged && changedAddress) {
    let isSelectedAddress = false;
    const foundAddressIndex = state.addressData.findIndex(
      item => item.id === changedAddress.id
    );

    const changedAddressData = {
      ...changedAddress,
      ward: {
        id: changedAddress.ward?.id,
        code: changedAddress.ward?.code,
        fullName: changedAddress.ward?.fullName,
        district: changedAddress.district,
        province: changedAddress.province,
      },
      district: changedAddress.district,
      province: changedAddress.province,
      isSelected: isSelectedAddress,
    };
    if (foundAddressIndex !== -1) {
      isSelectedAddress = state.addressData[foundAddressIndex].isSelected;
      state.addressData[foundAddressIndex] = changedAddressData;
    }
    state.address = changedAddressData;
  }

  if (deletedProductIDs && deletedProductIDs.length > 0) {
    state.selectedProducts = state.selectedProducts.filter(
      item => !deletedProductIDs.includes(item.id)
    );
  }

  if (
    deletedProductProposalPolicyIDs &&
    deletedProductProposalPolicyIDs.length > 0
  ) {
    state.selectedProducts = state.selectedProducts.filter(
      item => !deletedProductProposalPolicyIDs.includes(item.id)
    );
  }

  if (isPriceChanged) {
    state.selectedProducts.forEach(item => {
      const priceChangedProduct = priceChangedProducts.find(
        priceChangedItem =>
          priceChangedItem.id === item.id &&
          priceChangedItem.subID === item.subID
      );

      if (priceChangedProduct) {
        if (
          priceChangedProduct.areAllPriceUnitsDeleted ||
          priceChangedProduct.isOldPriceUnitDeleted ||
          priceChangedProduct.newPriceUnitList?.length
        ) {
          item.priceUnit = priceChangedProduct.newPriceUnitList.filter(
            priceUnit => priceUnit.isSelected
          );
        } else {
          const indexPriceUnit = item.priceUnit.findIndex(
            priceUnit => priceUnit.id == priceChangedProduct.newPriceUnit.id
          );
          if (indexPriceUnit == -1) {
            item.priceUnit.push(priceChangedProduct.newPriceUnit);
          } else {
            item.priceUnit.splice(
              indexPriceUnit,
              1,
              priceChangedProduct.newPriceUnit
            );
          }
        }

        item.selectedPriceUnit = priceChangedProduct.newPriceUnit;
        item.customerQuantity = priceChangedProduct.newCustomerQuantity;
        if (priceChangedProduct.currentPrice) {
          item.originPrice = priceChangedProduct.currentPrice;
        }
        if (priceChangedProduct.originPrice) {
          item.originPrice = priceChangedProduct.originPrice;
        }
        item.productPricePolicyID = priceChangedProduct.productPricePolicyID;
        item.proposalItemID = priceChangedProduct.proposalItemID;
      }
    });
  }

  if (isBillEntityShippingPolicyItemChanged) {
    state.shippingPolicyItems =
      changedBillEntityShippingPolicyItem.entityShippingPolicyItems;
    state.locationShippingFee =
      changedBillEntityShippingPolicyItem.newBillEntityShippingPolicyItem;
    state.shippingPolicyItems?.forEach(item => {
      item.isSelected = state.locationShippingFee.id == item.id;
    });
  }

  if (isBillEntityDistanceShippingFeeChanged) {
    state.distanceShippingFees =
      changedBillEntityDistanceShippingFee.entityDistanceShippingFees;
    state.distanceShippingFee =
      changedBillEntityDistanceShippingFee.newBillEntityDistanceShippingFee;
    state.distanceShippingFees?.forEach(item => {
      item.isSelected = state.distanceShippingFee.id == item.id;
    });
  }

  if (isBillEntityCustomShippingFeesChanged) {
    state.customShippingFees =
      changedBillEntityCustomShippingFees.entityCustomShippingFees;
  }
};

const handleResetBillData = (state: CreateBillInterface): void => {
  state.selectedProducts = [];
  state.deliveryMethod = ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY;
  state.paymentMethod = ORDER_PAYMENT_METHOD.CASH;
  state.summaryBill = initialSummaryBill;
  state.totalCustomerQuantity = 0;
  state.validationResult = {};
  state.prevCustomerNote = '';
  state.customerNote = '';
  state.supplierNote = '';
  state.bankAccount = undefined;
  state.images = [];
  state.deliveryNote = undefined;
  state.totalPrice = 0;
  state.returnMoney = 0;
  state.paidMoney = {};
  state.depositMoney = {};
  state.deductedMoney = 0;
  state.excessMoney = 0;
  state.isDebt = false;
  state.debtMoney = 0;
  state.createdAt = moment().toISOString();
  state.bankAccountID = undefined;
  state.entityPricePolicyID = initialState.entityPricePolicyID;
  state.oldEntityPricePolicyID = initialState.oldEntityPricePolicyID;

  state.oldEntityPricePolicyID = undefined;
  state.entityPricePolicyID = undefined;
  state.entityProposalPolicyID = undefined;

  state.promotions = [];
  state.selectedOrderSalePromotionCondition = {};
  state.addQuantityValue = 1;
  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
  state.shippingPolicyItems = (state.shippingPolicyItems || []).map(item => {
    item.isSelected = false;
    return item;
  });
  state.distanceShippingFees = (state.distanceShippingFees || []).map(item => {
    item.isSelected = false;
    return item;
  });
  state.customShippingFees = (state.customShippingFees || []).map(item => {
    item.isSelected = false;
    return item;
  });
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.isCheckingBillItemQuantity = false;
  state.isLoadingShippingPolicyItems = false;
  state.isLoadingDistanceShippingFees = false;
  state.isLoadingCustomShippingFees = false;
  state.orderID = undefined;
  state.orderUpdatedAt = undefined;
  state.isLoadingBillFromOrder = false;
};

const handleResetBillDataUpdateSupplier = (state: any): void => {
  state.selectedProducts = [];
  state.deliveryMethod = ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY;
  state.paymentMethod = ORDER_PAYMENT_METHOD.CASH;
  state.summaryBill = initialSummaryBill;
  state.totalCustomerQuantity = 0;
  state.validationResult = {};
  state.customerInfo = {};
  state.deliveryNote = undefined;
  state.address = undefined;
  state.addressData = [];
  state.prevCustomerNote = '';
  state.customerNote = '';
  state.supplierNote = '';
  state.bankAccount = undefined;
  state.images = [];
  state.totalPrice = 0;
  state.returnMoney = 0;
  state.paidMoney = {};
  state.depositMoney = {};
  state.deductedMoney = 0;
  state.excessMoney = 0;
  state.isDebt = false;
  state.debtMoney = 0;
  state.createdAt = moment().toISOString();
  state.bankAccountID = undefined;

  state.oldEntityPricePolicyID = undefined;
  state.entityPricePolicyID = undefined;
  state.entityProposalPolicyID = undefined;

  state.addQuantityValue = 1;
  state.customShippingFees = [];
  state.shippingPolicyItems = [];
  state.distanceShippingFees = [];
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
};

const handleResetBillDataUpdateCustomer = (state: any): void => {
  state.deliveryNote = undefined;
  state.address = undefined;
  state.addressData = [];
  state.deliveryFee = 0;
  state.deliveryFeeType = 0;
  state.locationShippingFee = undefined;
  state.distanceShippingFee = undefined;
};

export interface CreateBillInterface {
  shouldReloadCreateBillPage: boolean;
  shouldUpdateCreateBillData: boolean;
  selectedProducts: any[];
  productSearchResults: any[];

  bills: any[];
  chainSuppliersIDs: string[];
  supplierInfo?: SupplierInfoType;
  customerInfo?: any;

  deliveryMethod: ORDER_DELIVERY_METHOD;
  paymentMethod: ORDER_PAYMENT_METHOD;
  address: AddressData | undefined;
  addressData: AddressData[]; // List of partner addresses
  bankAccount: any | undefined;
  deliveryNote: any | undefined;
  images: any[];

  currentPage: number;
  totalPartnerDraftBills: number;
  partnerDraftBills: BillData[];
  summaryBill: SummaryBillType;
  validationResult: any;
  totalCustomerQuantity: number;

  prevCustomerNote: string;
  customerNote: string;
  supplierNote: string;

  discount: number;
  fee: number;
  deliveryFee: number;
  deliveryFeeType: number;
  choiceProductList: any[];
  errorsCalcWholesaleProductForPartnerAfterUpdateQuantity: string[];
  promotions?: any[];
  selectedOrderSalePromotionCondition?: any;

  totalPrice: number;
  returnMoney: number;
  bankAccountID?: string;

  depositMoney?: {
    cash?: number;
    internetBanking?: number;
    creditCard?: number;
  };

  paidMoney?: {
    cash?: number;
    internetBanking?: number;
    creditCard?: number;
  };

  isStockOut?: boolean;
  shouldCreateOrder?: boolean;

  deductedMoney?: number;
  excessMoney?: number;
  isDebt?: boolean;
  debtMoney?: number;
  createdAt?: string;
  oldEntityPricePolicyID?: string;
  entityPricePolicyID?: string;
  entityProposalPolicyID?: string;

  // tách dòng
  addQuantityValue: number;
  separateLine: boolean;

  // phí giao hàng
  isCheckingBillItemQuantity: boolean;
  isLoadingCustomShippingFees: boolean;
  customShippingFees?: any[];
  isLoadingDistanceShippingFees: boolean;
  distanceShippingFees?: any[];
  isLoadingShippingPolicyItems: boolean;
  shippingPolicyItems?: any[];
  locationShippingFee: any | undefined;
  distanceShippingFee: any | undefined;

  // tạo nhanh bill từ order
  orderID?: string;
  orderUpdatedAt?: string;
  isLoadingBillFromOrder?: boolean;
}

const initialSummaryBill: SummaryBillType = {
  debtMoney: BigInt(0),
  deductedMoney: BigInt(0),
  deliveryFeeType: 0,
  depositMoney: undefined,
  excessMoney: BigInt(0),
  isDebt: false,
  isValidBillReturnMoney: false,
  paidMoney: undefined,
  returnMoney: BigInt(0),
  totalDepositMoney: BigInt(0),
  totalPaidMoney: BigInt(0),
  deliveryFee: 0,
  totalPrice: BigInt(0),
  totalTemporaryPrice: BigInt(0),
  totalItem: 0,
  totalAdjustment: BigInt(0),
  adjustments: [],
};

const initPartnerInfo: PartnerInfoType = {
  customerID: '',
  customerOwnerID: '',
  chainCustomerID: '',
  partnerID: '',
  avatar: '',
  displayName: '',
  pricePolicyID: '',
  proposalPolicyID: '',
  note: [],
  phoneNumber: '',
  totalDebt: 0,
};

const initSupplierInfo: SupplierInfoType = {
  id: '',
  avatar: '',
  displayName: '',
};

const initialState: CreateBillInterface = {
  shouldReloadCreateBillPage: false,
  shouldUpdateCreateBillData: false,
  productSearchResults: [],
  selectedProducts: [],

  supplierInfo: initSupplierInfo,
  chainSuppliersIDs: [],
  customerInfo: null,
  bills: [],

  deliveryMethod: ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY,
  paymentMethod: ORDER_PAYMENT_METHOD.CASH,
  address: undefined,
  addressData: [],
  bankAccount: undefined,
  deliveryNote: undefined,
  images: [],

  currentPage: 0,
  totalPartnerDraftBills: 0,
  partnerDraftBills: [],

  summaryBill: initialSummaryBill,

  validationResult: {},
  totalCustomerQuantity: 0,

  prevCustomerNote: '',
  customerNote: '',
  supplierNote: '',
  discount: 0,
  fee: 0,

  deliveryFee: 0,
  deliveryFeeType: 0,

  choiceProductList: [],
  errorsCalcWholesaleProductForPartnerAfterUpdateQuantity: [],
  promotions: [],

  isStockOut: true,
  shouldCreateOrder: false,

  totalPrice: 0,
  returnMoney: 0,
  deductedMoney: 0,
  excessMoney: 0,
  isDebt: false,
  debtMoney: 0,
  createdAt: moment().toISOString(),

  bankAccountID: undefined,

  oldEntityPricePolicyID: undefined,
  entityPricePolicyID: undefined,
  entityProposalPolicyID: undefined,

  addQuantityValue: 1,
  separateLine: false,

  isCheckingBillItemQuantity: false,
  isLoadingCustomShippingFees: false,
  customShippingFees: [],
  isLoadingDistanceShippingFees: false,
  distanceShippingFees: [],
  isLoadingShippingPolicyItems: false,
  shippingPolicyItems: [],
  locationShippingFee: undefined,
  distanceShippingFee: undefined,

  orderID: undefined,
  orderUpdatedAt: undefined,
  isLoadingBillFromOrder: false,
};

const createBillSlice = createSlice({
  name: 'createBill',
  initialState,
  reducers: {
    resetCreateBillData(state) {
      state.shouldReloadCreateBillPage = true;
      state.customerInfo = null;
      state.supplierInfo = initSupplierInfo;
      handleResetBillData(state);
    },
    updatePartnerAddressDataCreateBill(state, { payload }) {
      state.addressData = payload;
    },
    updateSelectedPartnerAddressCreateBill(state, { payload }) {
      state.address = payload;
    },
    updateCustomerNoteCreateBill(state, { payload }) {
      state.customerNote = payload;
    },
    updateSupplierNoteCreateBill(state, { payload }) {
      state.supplierNote = payload;
    },
    updateBillsCreateBill(state, { payload }) {
      state.bills = payload;
    },
    updateSelectedProductsCreateBill(state, { payload }) {
      const newSelectedProducts = payload;
      state.selectedProducts = newSelectedProducts;

      if (newSelectedProducts.length === 0) {
        state.summaryBill = initialSummaryBill;
      }
    },
    updateSelectedProductCreateBill(
      state,
      {
        payload,
      }: {
        payload: {
          id: string;
          subID: string;
          quantity?: number;
          originPrice?: number;
        };
      }
    ) {
      const { id, subID, quantity, originPrice } = payload;
      const selectedProduct = state.selectedProducts.find(
        item => item.id == id && item.subID == subID
      );
      if (selectedProduct && quantity != undefined) {
        selectedProduct.customerQuantity = Number(quantity);
      }
      if (selectedProduct && originPrice != undefined) {
        selectedProduct.originPrice = Number(originPrice);
      }
      if (selectedProduct?.promotions && selectedProduct?.promotions.length) {
        selectedProduct.promotions.forEach(promotion => {
          if (promotion.type === PROMOTION_TYPE.RATE && promotion.rate > 0) {
            promotion.adjustment = Math.round(
              (selectedProduct.originPrice *
                selectedProduct.customerQuantity *
                promotion.rate) /
                100
            );
          } else if (
            promotion.type === PROMOTION_TYPE.AMOUNT &&
            promotion.amount > 0
          ) {
            const amount =
              promotion.amount >= selectedProduct.originPrice
                ? selectedProduct.originPrice
                : promotion.amount;

            promotion.adjustment = Math.round(
              amount * selectedProduct.customerQuantity
            );
          }
        });
      }
    },
    updateBillInfo(state, { payload }) {
      const orderInfo = payload;

      Object.keys(orderInfo).forEach(key => {
        if (key === 'customerInfo') {
          handleResetBillDataUpdateCustomer(state);
          state[key] = orderInfo[key];

          return;
        }

        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = orderInfo[key] || '';
        }

        if (orderInfo[key] != undefined) {
          state[key] = orderInfo[key];
        }
      });
    },
    updateBillInfoToCreateBill(state, { payload }) {
      const billInfo = payload;
      Object.keys(billInfo).forEach(key => {
        if (key === 'supplierInfo') {
          state.customerInfo = initPartnerInfo;
          handleResetBillDataUpdateSupplier(state);
        }

        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = state.entityPricePolicyID || '';
          state.entityPricePolicyID = billInfo[key];
        }

        if (billInfo[key] != undefined) {
          state[key] = billInfo[key];
        }
      });
    },
    updatePaymentMethodCreateBill(state, { payload }) {
      state.paymentMethod = payload.paymentMethod;
    },

    updateBankAccountInfoCreateBill(state, { payload }) {
      if (
        state.bankAccount &&
        payload.bankAccount &&
        payload.bankAccount.id == state.bankAccount.id
      ) {
        state.bankAccount = undefined;
      } else {
        state.bankAccount = payload.bankAccount;
      }
    },

    updateDeliveryNoteCreateBill(state, { payload }) {
      state.deliveryNote = payload.deliveryNote;
    },
    updateShouldUpdateCreateBillData(state, { payload }) {
      state.shouldUpdateCreateBillData = payload;
    },
    resetValidationResultCreateBill: state => {
      state.validationResult = {};
    },
    setChoiceProductListCreateBill(state, { payload }) {
      state.choiceProductList = payload;
    },
    resetErrorsCalcProductAfterUpdateQuantityCreateBill(state) {
      state.errorsCalcWholesaleProductForPartnerAfterUpdateQuantity = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(findListAddressForCreateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.addressData = data;

      if (state.addressData.length > 0) {
        const foundAddressIndex = state.addressData.findIndex(
          item => item.id === state.address?.id
        );
        if (foundAddressIndex != -1) {
          if (
            !compareAddress(state.address, state.addressData[foundAddressIndex])
          ) {
            state.address = state.addressData[foundAddressIndex];
          }
        } else {
          const selectedAddress = state.addressData.find(
            item => item.isSelected
          );
          if (selectedAddress) {
            state.address = selectedAddress;
          } else {
            state.address = state.addressData[0];
          }
        }
      } else {
        state.address = undefined;
      }
    });
    builder.addCase(createAddressForCreateBill.fulfilled, (state, action) => {
      const { data } = action.payload;

      const address = { ...data, isSupplierCreateForPartner: true };
      if (state.addressData.length === 0) {
        state.address = address;
      }
      state.addressData.push(address);
    });
    builder.addCase(updateAddressForCreateBill.fulfilled, (state, action) => {
      const { data } = action.payload;

      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      state.addressData[foundAddressIndex] = {
        ...data,
        isSupplierCreateForPartner: true,
      };
      if (state.address?.id === data?.id) {
        state.address = data;
      }
    });
    builder.addCase(removeAddressForCreateBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      const foundAddressIndex = state.addressData.findIndex(
        item => item.id === data?.id
      );
      if (foundAddressIndex !== -1) {
        state.addressData.splice(foundAddressIndex, 1);
      }
    });
    builder.addCase(
      checkBillItemQuantityForCreateBill.pending,
      (state, response) => {
        state.isCheckingBillItemQuantity = true;
      }
    );
    // calcWholesaleProductForPartnerAfterUpdateQuantity
    builder.addCase(
      checkBillItemQuantityForCreateBill.rejected,
      (state, response) => {
        state.isCheckingBillItemQuantity = false;
        if (response?.error?.message) {
          state.errorsCalcWholesaleProductForPartnerAfterUpdateQuantity = [
            ...state.errorsCalcWholesaleProductForPartnerAfterUpdateQuantity,
            response.error.message,
          ];
        }
      }
    );
    builder.addCase(
      checkBillItemQuantityForCreateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const { isUpdatingProductItem, body } = action.meta.arg;

        state.shouldUpdateCreateBillData = !isUpdatingProductItem;
        state.entityProposalPolicyID = body.entityProposalPolicyID;

        if (data.validationResult && !data.validationResult.isValid) {
          state.shouldUpdateCreateBillData = true;
          state.validationResult = data.validationResult;
          updateStore(state, data, true);
          updateProductsWithValidationResult(state);
        } else {
          updateStore(state, data, !isUpdatingProductItem);
        }
        state.isCheckingBillItemQuantity = false;

        let newSelectedProductItems = [...current(state).selectedProducts];

        newSelectedProductItems = [...newSelectedProductItems].map(
          (item: any) => {
            const foundSelectedProduct = (data?.selectedProducts).find(
              (selectedProduct: any) =>
                selectedProduct.productID == item.id &&
                selectedProduct.subID == item.subID
            );

            if (foundSelectedProduct) {
              return {
                ...item,
                promotions: foundSelectedProduct.promotions,
                salePromotionConditions:
                  foundSelectedProduct.salePromotionConditions,
              };
            }

            return item;
          }
        );
        state.selectedProducts = [...newSelectedProductItems];
      }
    );
    builder.addCase(createDraftBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      if (data.validationResult && !data.validationResult.isValid) {
        updateStore(state, data, true);
        updateProductsWithValidationResult(state);
        state.validationResult = data.validationResult;
      } else {
        handleResetBillData(state);
        const { order } = data;
        state.partnerDraftBills.unshift({ ...order, pageIndex: 1 });
        state.bills = [
          {
            ...data.bill,
            supplier: state.supplierInfo,
            customer: state.customerInfo,
          },
          ...state.bills,
        ];
      }
    });
    builder.addCase(createBill.fulfilled, (state, action) => {
      const { data } = action.payload;
      if (data.validationResult && !data.validationResult.isValid) {
        updateStore(state, data, true);
        updateProductsWithValidationResult(state);
        state.shouldUpdateCreateBillData = true;
        state.validationResult = data.validationResult;
      } else {
        handleResetBillData(state);
        state.bills = [
          {
            ...data.bill,
            supplier: state.supplierInfo,
            customer: state.customerInfo,
          },
          ...state.bills,
        ];
      }
    });
    builder.addCase(
      findEntityCustomShippingFeeToCreateBill.pending,
      (state, response) => {
        state.isLoadingCustomShippingFees = true;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateBill.rejected,
      (state, response) => {
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.customShippingFees = data?.map(item => {
          const foundItem = (
            state.summaryBill?.deliveryFeeDetail?.customShippingFees || []
          ).find(item1 => item1.id == item.id);
          if (foundItem) {
            return {
              ...item,
              ...foundItem,
              isSelected: true,
            };
          }
          return { ...item, isSelected: false };
        });
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateBill.pending,
      (state, response) => {
        state.isLoadingDistanceShippingFees = true;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateBill.rejected,
      (state, response) => {
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.distanceShippingFees = data?.map(item => {
          if (
            state.summaryBill?.deliveryFeeDetail?.entityDistanceShippingFee
              ?.id == item.id
          ) {
            return {
              ...item,
              ...state.summaryBill?.deliveryFeeDetail
                ?.entityDistanceShippingFee,
              isSelected: true,
            };
          }
          return { ...item, isSelected: false };
        });
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateBill.pending,
      (state, response) => {
        state.isLoadingShippingPolicyItems = true;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateBill.rejected,
      (state, response) => {
        state.isLoadingShippingPolicyItems = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateBill.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.shippingPolicyItems = data?.map(item => {
          if (
            state.summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem
              ?.id == item.id
          ) {
            return {
              ...item,
              ...state.summaryBill?.deliveryFeeDetail?.entityShippingPolicyItem,
              isSelected: true,
            };
          }
          return { ...item, isSelected: false };
        });
        state.isLoadingShippingPolicyItems = false;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateBillFromOrder.pending,
      (state, response) => {
        state.isLoadingCustomShippingFees = true;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateBillFromOrder.rejected,
      (state, response) => {
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToCreateBillFromOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.customShippingFees = data?.map(item => {
          item.isSelected = item.orderID != undefined;
          return item;
        });
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateBillFromOrder.pending,
      (state, response) => {
        state.isLoadingDistanceShippingFees = true;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateBillFromOrder.rejected,
      (state, response) => {
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToCreateBillFromOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.distanceShippingFees = data?.map(item => {
          item.isSelected = item.orderID != undefined;
          if (item.isSelected) {
            state.distanceShippingFee = item;
          }
          return item;
        });
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateBillFromOrder.pending,
      (state, response) => {
        state.isLoadingShippingPolicyItems = true;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateBillFromOrder.rejected,
      (state, response) => {
        state.isLoadingShippingPolicyItems = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToCreateBillFromOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.shippingPolicyItems = data?.map(item => {
          item.isSelected = item.orderID != undefined;
          if (item.isSelected) {
            state.locationShippingFee = item;
          }
          return item;
        });
        state.isLoadingShippingPolicyItems = false;
      }
    );
  },
});

const { actions, reducer: createBillReducer } = createBillSlice;

export const {
  updateSelectedProductsCreateBill,
  updateSelectedProductCreateBill,
  updateBillInfo,
  updateBillsCreateBill,
  updateCustomerNoteCreateBill,
  updateSupplierNoteCreateBill,
  updateShouldUpdateCreateBillData,
  resetCreateBillData,
  resetValidationResultCreateBill,
  setChoiceProductListCreateBill,
  resetErrorsCalcProductAfterUpdateQuantityCreateBill,
  updatePaymentMethodCreateBill,
  updateBillInfoToCreateBill,
  updateBankAccountInfoCreateBill,
  updateDeliveryNoteCreateBill,
} = actions;

export const selectCreateBill = (state: RootState): CreateBillInterface =>
  state.createBill as CreateBillInterface;

export default createBillReducer;
